<template>
  <v-container fluid>
    <!-- สถิติภาพรวม -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card color="primary" dark>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
                <div class="text-subtitle-2">รายงานทั้งหมด</div>
              </div>
              <v-icon size="48" class="opacity-50">mdi-certificate</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card color="warning" dark>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.pending }}</div>
                <div class="text-subtitle-2">รอตรวจสอบ</div>
              </div>
              <v-icon size="48" class="opacity-50">mdi-clock-alert</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card color="success" dark>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.approved }}</div>
                <div class="text-subtitle-2">อนุมัติแล้ว</div>
              </div>
              <v-icon size="48" class="opacity-50">mdi-check-circle</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card color="error" dark>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.rejected }}</div>
                <div class="text-subtitle-2">ส่งกลับแก้ไข</div>
              </div>
              <v-icon size="48" class="opacity-50">mdi-close-circle</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-shield-check</v-icon>
        ตรวจสอบและอนุมัติใบอนุญาตประกอบวิชาชีพ
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          outlined
          @click="exportToExcel"
          :loading="exporting"
        >
          <v-icon left>mdi-file-excel</v-icon>
          ส่งออก Excel
        </v-btn>
      </v-card-title>

      <!-- ตัวกรอง -->
      <v-card-text>
        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <v-icon left>mdi-filter</v-icon>
                ตัวกรองข้อมูล
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filter.approval_status"
                    :items="approvalStatusOptions"
                    label="สถานะการอนุมัติ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-check-circle"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="3">
                  <v-select
                    v-model="filter.license_status"
                    :items="licenseStatusOptions"
                    label="สถานะใบอนุญาต"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-certificate"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.license_type_id"
                    :items="licenseTypes"
                    item-text="license_type_name"
                    item-value="license_type_id"
                    label="ประเภทใบอนุญาต"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-certificate-outline"
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_text"
                    label="ค้นหา (ชื่อ/เลขบัตร)"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-magnify"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" class="text-right">
                  <v-btn
                    color="primary"
                    @click="loadLicenses"
                    :loading="loading"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    ค้นหา
                  </v-btn>
                  <v-btn color="grey" text @click="clearFilter" class="ml-2">
                    <v-icon left>mdi-refresh</v-icon>
                    ล้างตัวกรอง
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- ตารางแสดงข้อมูล -->
        <v-data-table
          :headers="headers"
          :items="licenses"
          :loading="loading"
          :search="searchTable"
          class="elevation-1"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50]
          }"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>รายการรอตรวจสอบ</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchTable"
                append-icon="mdi-magnify"
                label="ค้นหาในตาราง"
                single-line
                hide-details
                dense
                outlined
              ></v-text-field>
            </v-toolbar>
          </template>

          <template v-slot:item.full_name="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" class="mr-2" color="primary">
                <span class="white--text text-subtitle-2">{{
                  getInitials(item.full_name)
                }}</span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.full_name }}</div>
                <div class="caption grey--text">{{ item.mt_id_card }}</div>
              </div>
            </div>
          </template>

          <template v-slot:item.license_type_name="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.license_type_name }}</div>
              <div class="caption text--secondary">
                {{ item.license_number }}
              </div>
            </div>
          </template>

          <template v-slot:item.expiry_date="{ item }">
            <div>
              <div>{{ formatDate(item.expiry_date) }}</div>
              <v-chip
                v-if="item.days_until_expiry !== null"
                x-small
                :color="getExpiryColor(item.days_until_expiry)"
                text-color="white"
                class="mt-1"
              >
                {{ getExpiryText(item.days_until_expiry) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.license_status="{ item }">
            <v-chip
              small
              :color="getLicenseStatusColor(item.license_status)"
              text-color="white"
            >
              {{ getLicenseStatusText(item.license_status) }}
            </v-chip>
          </template>

          <template v-slot:item.approval_status="{ item }">
            <v-chip
              small
              :color="getApprovalStatusColor(item.approval_status)"
              text-color="white"
            >
              <v-icon left small>{{
                getApprovalStatusIcon(item.approval_status)
              }}</v-icon>
              {{ getApprovalStatusText(item.approval_status) }}
            </v-chip>
          </template>

          <template v-slot:item.license_file="{ item }">
            <v-btn
              v-if="item.license_file"
              icon
              small
              color="primary"
              @click="viewFile(item.license_file)"
            >
              <v-icon>mdi-file-document</v-icon>
            </v-btn>
            <span v-else class="grey--text">-</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="info"
                  class="mr-2"
                  @click="viewDetail(item)"
                  v-on="on"
                >
                  mdi-eye
                </v-icon>
              </template>
              <span>ดูรายละเอียด</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'pending'">
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="success"
                  class="mr-2"
                  @click="openApprovalDialog(item, 'approved')"
                  v-on="on"
                >
                  mdi-check-circle
                </v-icon>
              </template>
              <span>อนุมัติ</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'pending'">
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="error"
                  @click="openApprovalDialog(item, 'rejected')"
                  v-on="on"
                >
                  mdi-close-circle
                </v-icon>
              </template>
              <span>ส่งกลับแก้ไข</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog อนุมัติ/ส่งกลับ -->
    <v-dialog v-model="dialogApproval" max-width="600px" persistent>
      <v-card>
        <v-card-title
          :class="
            approvalAction === 'approved'
              ? 'success white--text'
              : 'error white--text'
          "
        >
          <v-icon left color="white">
            {{
              approvalAction === "approved"
                ? "mdi-check-circle"
                : "mdi-close-circle"
            }}
          </v-icon>
          {{ approvalAction === "approved" ? "อนุมัติรายงาน" : "ส่งกลับแก้ไข" }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert
            :type="approvalAction === 'approved' ? 'success' : 'warning'"
            text
            prominent
            border="left"
          >
            <div class="font-weight-medium">
              {{
                approvalAction === "approved"
                  ? "ยืนยันการอนุมัติรายงานใบอนุญาต"
                  : "ส่งรายงานกลับเพื่อแก้ไข"
              }}
            </div>
            <div class="mt-2">
              <strong>ครูผู้ช่วย:</strong> {{ selectedItem.full_name }}<br />
              <strong>ประเภทใบอนุญาต:</strong>
              {{ selectedItem.license_type_name }}<br />
              <strong>เลขที่:</strong> {{ selectedItem.license_number }}
            </div>
          </v-alert>

          <v-form ref="approvalForm" v-model="approvalValid">
            <v-textarea
              v-model="approvalData.reviewer_remarks"
              :label="
                approvalAction === 'approved'
                  ? 'หมายเหตุ (ถ้ามี)'
                  : 'เหตุผลที่ส่งกลับแก้ไข *'
              "
              outlined
              :rules="approvalAction === 'rejected' ? [rules.required] : []"
              rows="4"
              prepend-inner-icon="mdi-note-text"
              hint="โปรดระบุรายละเอียดเพิ่มเติม"
              persistent-hint
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogApproval = false"
            >ยกเลิก</v-btn
          >
          <v-btn
            :color="approvalAction === 'approved' ? 'success' : 'error'"
            :loading="approving"
            @click="submitApproval"
          >
            <v-icon left>
              {{
                approvalAction === "approved" ? "mdi-check" : "mdi-arrow-left"
              }}
            </v-icon>
            {{ approvalAction === "approved" ? "อนุมัติ" : "ส่งกลับแก้ไข" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog แสดงรายละเอียด -->
    <v-dialog v-model="dialogDetail" max-width="800px">
      <v-card v-if="selectedItem">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-information</v-icon>
          รายละเอียดใบอนุญาต
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <!-- ข้อมูลครูผู้ช่วย -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle class="font-weight-bold">
                  <v-icon small class="mr-1">mdi-account</v-icon>
                  ข้อมูลครูผู้ช่วย
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >ชื่อ-นามสกุล</v-list-item-subtitle
                        >
                        <v-list-item-title class="text-h6">{{
                          selectedItem.full_name
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >เลขบัตรประชาชน</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          selectedItem.id_card
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- ข้อมูลใบอนุญาต -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle class="font-weight-bold">
                  <v-icon small class="mr-1">mdi-certificate</v-icon>
                  ข้อมูลใบอนุญาต
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >ประเภทใบอนุญาต</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          selectedItem.license_type_name
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >เลขที่ใบอนุญาต</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          selectedItem.license_number
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >หน่วยงานที่ออก</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          selectedItem.issuer_organization || "-"
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >วันที่ออกใบอนุญาต</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          formatDate(selectedItem.issue_date)
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >วันที่หมดอายุ</v-list-item-subtitle
                        >
                        <v-list-item-title>
                          {{ formatDate(selectedItem.expiry_date) }}
                          <v-chip
                            v-if="selectedItem.days_until_expiry !== null"
                            x-small
                            class="ml-2"
                            :color="
                              getExpiryColor(selectedItem.days_until_expiry)
                            "
                            text-color="white"
                          >
                            {{ getExpiryText(selectedItem.days_until_expiry) }}
                          </v-chip>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >สถานะใบอนุญาต</v-list-item-subtitle
                        >
                        <v-list-item-title>
                          <v-chip
                            small
                            :color="
                              getLicenseStatusColor(selectedItem.license_status)
                            "
                            text-color="white"
                          >
                            {{
                              getLicenseStatusText(selectedItem.license_status)
                            }}
                          </v-chip>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.remarks">
                      <v-list-item-content>
                        <v-list-item-subtitle>หมายเหตุ</v-list-item-subtitle>
                        <v-list-item-title>{{
                          selectedItem.remarks
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.license_file">
                      <v-list-item-content>
                        <v-list-item-subtitle>ไฟล์แนบ</v-list-item-subtitle>
                        <v-list-item-title>
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="viewFile(selectedItem.license_file)"
                          >
                            <v-icon left small>mdi-file-document</v-icon>
                            ดูไฟล์
                          </v-btn>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- สถานะการอนุมัติ -->
            <v-col cols="12">
              <v-card
                outlined
                :color="
                  getApprovalStatusColor(selectedItem.approval_status) +
                    ' lighten-5'
                "
              >
                <v-card-subtitle class="font-weight-bold">
                  <v-icon small class="mr-1">mdi-clipboard-check</v-icon>
                  สถานะการอนุมัติ
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle>สถานะ</v-list-item-subtitle>
                        <v-list-item-title>
                          <v-chip
                            :color="
                              getApprovalStatusColor(
                                selectedItem.approval_status
                              )
                            "
                            text-color="white"
                          >
                            <v-icon left small>{{
                              getApprovalStatusIcon(
                                selectedItem.approval_status
                              )
                            }}</v-icon>
                            {{
                              getApprovalStatusText(
                                selectedItem.approval_status
                              )
                            }}
                          </v-chip>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.reviewer_name">
                      <v-list-item-content>
                        <v-list-item-subtitle>ผู้ตรวจสอบ</v-list-item-subtitle>
                        <v-list-item-title>{{
                          selectedItem.reviewer_name
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.review_date">
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >วันที่ตรวจสอบ</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          formatDate(selectedItem.review_date)
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.reviewer_remarks">
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >หมายเหตุจากผู้ตรวจสอบ</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          selectedItem.reviewer_remarks
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- ข้อมูลการบันทึก -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle class="font-weight-bold">
                  <v-icon small class="mr-1">mdi-information-outline</v-icon>
                  ข้อมูลการบันทึก
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >วันที่บันทึก</v-list-item-subtitle
                        >
                        <v-list-item-title>{{
                          formatDate(selectedItem.survey_date)
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="selectedItem.surveyor_name">
                      <v-list-item-content>
                        <v-list-item-subtitle>ผู้บันทึก</v-list-item-subtitle>
                        <v-list-item-title>{{
                          selectedItem.surveyor_name
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectedItem.approval_status === 'pending'"
            color="success"
            @click="openApprovalDialog(selectedItem, 'approved')"
          >
            <v-icon left>mdi-check-circle</v-icon>
            อนุมัติ
          </v-btn>
          <v-btn
            v-if="selectedItem.approval_status === 'pending'"
            color="error"
            @click="openApprovalDialog(selectedItem, 'rejected')"
          >
            <v-icon left>mdi-close-circle</v-icon>
            ส่งกลับแก้ไข
          </v-btn>
          <v-btn color="grey" text @click="dialogDetail = false">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdflicense_filedialog" max-width="80%">
      <v-card class="" elevation="2">
        <embed
          :src="'/HRvecLicenseTeach/' + license_file"
          width="100%"
          height="700px"
        />
      </v-card>
    </v-dialog>
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: "AdminCollegeLicenseApproval",
  data() {
    return {
      ApiKey: "HRvec2021",
      pdflicense_filedialog: false,
      license_file: "",
      userSession: null,
      currentUser: null,
      loading: false,
      approving: false,
      exporting: false,
      approvalValid: false,
      dialogApproval: false,
      dialogDetail: false,
      searchTable: "",

      // ข้อมูลหลัก
      licenses: [],
      licenseTypes: [],
      selectedItem: [],
      approvalAction: null,

      // สถิติ
      stats: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
      },

      // ตัวกรอง
      filter: {
        approval_status: null,
        license_status: null,
        license_type_id: null,
        search_text: ""
      },

      // ข้อมูลการอนุมัติ
      approvalData: {
        license_id: null,
        approval_status: null,
        reviewer_remarks: "",
        reviewer_name: "",
        review_date: new Date().toISOString().substr(0, 10)
      },

      // Options
      approvalStatusOptions: [
        { text: "รอตรวจสอบ", value: "pending" },
        { text: "อนุมัติแล้ว", value: "approved" },
        { text: "ส่งกลับแก้ไข", value: "rejected" }
      ],

      licenseStatusOptions: [
        { text: "ใช้งานได้", value: "active" },
        { text: "หมดอายุ", value: "expired" },
        { text: "ถูกเพิกถอน", value: "revoked" },
        { text: "รอดำเนินการ", value: "pending" }
      ],

      // Headers
      headers: [
        { text: "ครูผู้ช่วย", value: "full_name", width: 200 },
        { text: "ประเภทใบอนุญาต", value: "license_type_name", width: 250 },
        {
          text: "วันที่หมดอายุ",
          value: "expiry_date",
          align: "center",
          width: 150
        },
        {
          text: "สถานะใบอนุญาต",
          value: "license_status",
          align: "center",
          width: 130
        },
        {
          text: "สถานะการอนุมัติ",
          value: "approval_status",
          align: "center",
          width: 150
        },
        {
          text: "ไฟล์",
          value: "license_file",
          align: "center",
          sortable: false,
          width: 80
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "center",
          sortable: false,
          width: 150
        }
      ],

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "success"
      },

      // Rules
      rules: {
        required: v => !!v || "กรุณากรอกข้อมูล"
      }
    };
  },

  async mounted() {
    await this.initializeUser();
  },

  methods: {
    async initializeUser() {
      try {
        this.loading = true;
        this.userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        await this.loadCurrentUser();
        await this.initialize();
      } catch (error) {
        console.error("Error initializing user:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadCurrentUser() {
      try {
        const response = await this.$http.post("admin.php", {
          ApiKey: this.ApiKey,
          user_name: this.userSession.user_name
        });

        if (response.data) {
          this.currentUser = response.data;
        }
      } catch (error) {
        console.error("Error loading current user:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      }
    },

    async initialize() {
      await Promise.all([
        this.loadLicenseTypes(),
        this.loadLicenses(),
        this.loadStatistics()
      ]);
    },

    async loadLicenseTypes() {
      try {
        const response = await this.$http.post(
          "specialized_license_types.php",
          {
            ApiKey: this.ApiKey,
            is_active: 1
          }
        );
        this.licenseTypes = response.data || [];
      } catch (error) {
        console.error("Error loading license types:", error);
      }
    },

    async loadLicenses() {
      this.loading = true;
      try {
        const params = {
          ApiKey: this.ApiKey,
          college_code: this.currentUser.college_code
        };

        const response = await this.$http.post(
          "assistant_specialized_licenses.php",
          params
        );
        this.licenses = response.data || [];
      } catch (error) {
        console.error("Error loading licenses:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadStatistics() {
      try {
        const response = await this.$http.post(
          "assistant_specialized_licenses.php",
          {
            ApiKey: this.ApiKey,
            get_statistics: true,
            college_code: this.currentUser.college_code
          }
        );

        if (response.data) {
          this.stats = {
            total: parseInt(response.data.total) || 0,
            draft: parseInt(response.data.draft) || 0,
            pending: parseInt(response.data.pending) || 0,
            approved: parseInt(response.data.approved) || 0,
            rejected: parseInt(response.data.rejected) || 0
          };
        }
      } catch (error) {
        console.error("Error loading statistics:", error);
      }
    },

    clearFilter() {
      this.filter = {
        approval_status: null,
        license_status: null,
        license_type_id: null,
        search_text: ""
      };
      this.loadLicenses();
    },

    openApprovalDialog(item, action) {
      this.selectedItem = item;
      this.approvalAction = action;
      this.approvalData = {
        license_id: item.license_id,
        approval_status: action,
        reviewer_remarks: "",
        reviewer_name: `${this.currentUser.user_firstname}`,
        review_date: new Date().toISOString().substr(0, 10)
      };
      this.dialogDetail = false;
      this.dialogApproval = true;
    },

    async submitApproval() {
      if (
        this.approvalAction === "rejected" &&
        !this.$refs.approvalForm.validate()
      ) {
        this.showSnackbar("กรุณากรอกเหตุผลที่ส่งกลับแก้ไข", "warning");
        return;
      }

      this.approving = true;
      try {
        const response = await this.$http.post(
          "assistant_specialized_licenses.approve.php",
          {
            ApiKey: this.ApiKey,
            ...this.approvalData,
            reviewer_id_card: this.currentUser.id_card
          }
        );

        if (response.data.status) {
          const message =
            this.approvalAction === "approved"
              ? "อนุมัติรายงานสำเร็จ"
              : "ส่งกลับแก้ไขสำเร็จ";
          this.showSnackbar(message, "success");
          this.dialogApproval = false;
          await this.loadLicenses();
          await this.loadStatistics();
        } else {
          this.showSnackbar(response.data.message || "เกิดข้อผิดพลาด", "error");
        }
      } catch (error) {
        console.error("Error submitting approval:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึกข้อมูล", "error");
      } finally {
        this.approving = false;
      }
    },

    viewDetail(item) {
      this.selectedItem = item;
      this.dialogDetail = true;
    },

   viewFile(fileName) {
      if (fileName) {
        this.license_file = "";
        this.license_file = fileName;
        this.pdflicense_filedialog = true;
      }
    },

    async exportToExcel() {
      this.exporting = true;
      try {
        const XLSX = await import("xlsx");

        const data = this.licenses.map(item => ({
          "ชื่อ-นามสกุล": item.full_name,
          เลขบัตรประชาชน: item.id_card,
          ประเภทใบอนุญาต: item.license_type_name,
          เลขที่ใบอนุญาต: item.license_number,
          หน่วยงานออก: item.issuer_organization || "-",
          วันที่ออก: this.formatDate(item.issue_date),
          วันที่หมดอายุ: this.formatDate(item.expiry_date),
          จำนวนวันคงเหลือ:
            item.days_until_expiry !== null ? item.days_until_expiry : "-",
          สถานะใบอนุญาต: this.getLicenseStatusText(item.license_status),
          สถานะการอนุมัติ: this.getApprovalStatusText(item.approval_status),
          ผู้ตรวจสอบ: item.reviewer_name || "-",
          วันที่ตรวจสอบ: item.review_date
            ? this.formatDate(item.review_date)
            : "-",
          หมายเหตุผู้ตรวจสอบ: item.reviewer_remarks || "-",
          ผู้บันทึก: item.surveyor_name,
          วันที่บันทึก: this.formatDate(item.survey_date)
        }));

        const ws = XLSX.utils.json_to_sheet(data);

        // ปรับความกว้างคอลัมน์
        const colWidths = [
          { wch: 25 }, // ชื่อ-นามสกุล
          { wch: 15 }, // เลขบัตร
          { wch: 30 }, // ประเภท
          { wch: 20 }, // เลขที่
          { wch: 25 }, // หน่วยงาน
          { wch: 12 }, // วันที่ออก
          { wch: 12 }, // หมดอายุ
          { wch: 12 }, // วันคงเหลือ
          { wch: 15 }, // สถานะใบอนุญาต
          { wch: 15 }, // สถานะอนุมัติ
          { wch: 25 }, // ผู้ตรวจ
          { wch: 12 }, // วันตรวจ
          { wch: 30 }, // หมายเหตุ
          { wch: 25 }, // ผู้บันทึก
          { wch: 12 } // วันบันทึก
        ];
        ws["!cols"] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "รายงานใบอนุญาต");

        const fileName = `license_report_${
          this.currentUser.college_code
        }_${new Date().toISOString().substr(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);

        this.showSnackbar("ส่งออกข้อมูลสำเร็จ", "success");
      } catch (error) {
        console.error("Error exporting:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออกข้อมูล", "error");
      } finally {
        this.exporting = false;
      }
    },

    getInitials(fullName) {
      if (!fullName) return "";
      const parts = fullName.trim().split(" ");
      if (parts.length >= 2) {
        return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      }
      return fullName.charAt(0);
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const day = d
        .getDate()
        .toString()
        .padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const year = d.getFullYear() + 543;
      return `${day}/${month}/${year}`;
    },

    getLicenseStatusColor(status) {
      const colors = {
        active: "success",
        expired: "error",
        revoked: "grey",
        pending: "warning"
      };
      return colors[status] || "grey";
    },

    getLicenseStatusText(status) {
      const texts = {
        active: "ใช้งานได้",
        expired: "หมดอายุ",
        revoked: "ถูกเพิกถอน",
        pending: "รอดำเนินการ"
      };
      return texts[status] || status;
    },

    getApprovalStatusColor(status) {
      const colors = {
        pending: "warning",
        approved: "success",
        rejected: "error"
      };
      return colors[status] || "grey";
    },

    getApprovalStatusText(status) {
      const texts = {
        pending: "รอตรวจสอบ",
        approved: "อนุมัติแล้ว",
        rejected: "ส่งกลับแก้ไข"
      };
      return texts[status] || status;
    },

    getApprovalStatusIcon(status) {
      const icons = {
        pending: "mdi-clock-alert",
        approved: "mdi-check-circle",
        rejected: "mdi-close-circle"
      };
      return icons[status] || "mdi-help-circle";
    },

    getExpiryColor(days) {
      if (days < 0) return "error";
      if (days <= 30) return "error";
      if (days <= 90) return "warning";
      return "success";
    },

    getExpiryText(days) {
      if (days < 0) return "หมดอายุแล้ว";
      if (days === 0) return "หมดอายุวันนี้";
      if (days <= 30) return `เหลือ ${days} วัน`;
      if (days <= 90) return `เหลือ ${days} วัน`;
      return `เหลือ ${days} วัน`;
    },

    showSnackbar(text, color = "success") {
      this.snackbar = {
        show: true,
        text,
        color
      };
    }
  }
};
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.opacity-50 {
  opacity: 0.5;
}
</style>
